/* eslint-disable react/default-props-match-prop-types */
import React from 'react';
import { useDevice } from '@bonnet/next/device';
import PropTypes from 'prop-types';
import {
    Button,
    PageSection,
    TabNavSection,
} from 'reaxl';

import { newsReviewsAnalytics } from '@/analytics';
import NewsReviews from '@/component/NewsReviews';
import getUrlByPageValue from '@/utilities/getUrlByPageValue';

const COMMERCIAL_PAGE = 'commercial';

const getConfigByPage = ({ pageType, pageValue }) => ({
    CONDITION: {
        navUrl: getUrlByPageValue(pageValue),
    },
    HOWTOBUYCARS: {
        navUrl: '/car-topics/buy-cars-online',
    },
    CARSUB: {
        navUrl: '/car-topics/car-subscription-services',
    },
    STYLE: {
        navUrl: `/topic/${pageValue}`,
    },
    MAKE: {
        navUrl: (() => {
            // Special cases for fiat, rolls-royce, rivian
            switch (pageValue.toLowerCase()) {
                case 'fiat':
                    return '/article-search-results/fiat';
                case 'rolls-royce':
                    return '/article-search-results/Rolls-Royce';
                case 'rivian':
                    return '/article-search-results/rivian';
                default:
                    // Fallback to the existing `/topic` route
                    return `/topic/${pageValue}`;
            }
        })(),
    },
    MODEL: {
        navUrl: `/car-news/${pageValue?.replace('/', '-')}.xhtml`,
    },
    TRIM: {
        navUrl: `/car-news/${pageValue?.replace('/', '-')}.xhtml`,
    },
    MODELINFO: {
        navUrl: '/car-topics/latest-articles.xhtml',
    },
    HOMEPAGE: {
        navUrl: '/car-news',
    },
    ATHOMESERVICES: {
        navUrl: '/article-search-results/car%20shopping',
    },
    OTHER: {
        navUrl: '/car-news',
    },
    PAYMENTCALCULATOR: {
        navUrl: '/topic/finance',
    },
}[pageType]);

const getNavUrl = (pageType, pageValue) => {
    let pageData = {};
    if (pageValue === COMMERCIAL_PAGE) {
        return '/topic/commercial';
    }
    switch (pageType) {
        case 'TRIM': {
            // News Reviews does not have Trim level - Use only make/Model
            const makeModelValue = pageValue.split('/').slice(0, 2).join('/');
            pageData = getConfigByPage({ pageType, pageValue: makeModelValue });
            break;
        }
        default:
            pageData = getConfigByPage({ pageType, pageValue });
    }
    if (Object.keys(pageData).length > 0 && pageData.navUrl) {
        return pageData.navUrl;
    }
    return '/car-news';
};

const renderButton = (pageType, pageValue, label, customAnalytics) => {
    const handelOnClick = (e) => {
        newsReviewsAnalytics.fireReaxlBtnCTAClick(e, { label, ...customAnalytics });
    };

    const browseButtonLabel = {
        label,
        href: getNavUrl(pageType, pageValue),
    };

    return (
        <div
            className="text-center justify-content-center justify-content-sm-start cta-wrapper"
        >
            <Button
                bsStyle="primary"
                className="btn btn-primary"
                onClick={handelOnClick}
                {...browseButtonLabel}
            >
                {browseButtonLabel.label}
            </Button>
        </div>
    );
};

export const removeArticleImageParam = (url, param) => {
    try {
        if (!url) {
            return url;
        }
        const urlObj = new URL(url);

        if (urlObj.searchParams.get(param)) {
            urlObj.searchParams.delete(param);
        }

        return urlObj.href;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.log('Error while excuting function removeArticleImageParam', error);
        return url;
    }

};

export const articleImageURLScaler = (articles) => {
    const replaceWidth = /\b(w=)([^&]*)/ig;
    const replaceHeight = /\b(h=)([^&]*)/ig;

    return articles.map((article, idx) => {
        if (idx > 0 && article.src) {
            article.src = article.src.replace(replaceWidth, 'w=248').replace(replaceHeight, 'h=165');
        }

        // remove param crop
        article.src = removeArticleImageParam(article.src, 'crop');

        return article;
    });
};

const NewsReviewsContainer = React.memo(({
    articles = [],
    isCollapsed,
    actions,
    pageType,
    pageValue,
    heading,
    className,
    headingClassName,
    headingComponentClass,
    sectionContainerClassNames,
    uiContext,
    customCtaText = '',
    customAnalytics = {},
    showCTA = true,
}) => {

    pageType = (String(pageType)).toUpperCase();
    const device = useDevice();
    const sectionHeading = pageValue === 'used' ? 'Used Car Shopping & Buying Tips' : 'Car Shopping & Buying Tips';
    const ctaText = customCtaText || (heading + ' News & Reviews');
    const handleArticleClick = (e, article, data) => {
        let referenceData;
        if (data) {
            referenceData = data?.referenceData;
        }
        if (article) {
            const index = articles.findIndex((a) => a.href === article.href);
            const articleTitle = article.title;
            newsReviewsAnalytics.fireReaxlArticleClick(e, { index, articleTitle, referenceData });
        }
    };

    return articles.length > 0 && (
        <TabNavSection eventKey="key6">
            <PageSection
                data-cmp="news-reviews"
                uiContext={uiContext || 'section-2'}
                headingComponentClass={headingComponentClass || 'h2'}
                heading={sectionHeading}
                alignment="left"
                offset={1}
                headingClassName={headingClassName}
                containerClassname="news-reviews-container"
                sectionContainerClassNames={sectionContainerClassNames}
                headingProps={
                    {
                        role: 'heading',
                        'aria-level': 2,
                    }
                }
            >
                <NewsReviews
                    collapsed={device.is.xs}
                    articles={articleImageURLScaler(articles)}
                    onClick={handleArticleClick}
                    className={className}
                />
                { showCTA && renderButton(pageType, pageValue, ctaText, customAnalytics) }
            </PageSection>
        </TabNavSection>
    );
});

NewsReviewsContainer.propTypes = {
    articles: PropTypes.array,
};

export default NewsReviewsContainer;
